import { OwlLinkAdditionalData } from '@src/components/Cards/types'
import { CardContent, CardContentWithoutEK } from '@src/components/Editor/types'
import { EditorState, RawDraftContentState } from 'draft-js'
import { GetPaperSubject_getPaperSubjectPrediction_metadata } from 'src/queries/GetPaperSubject'
import { createAsyncAction, createStandardAction } from 'typesafe-actions'
import { DocumentState } from './reducer'
import { CheckCategory } from './types'

export interface DocumentInfo {
  id: string
  data?: any
}

export interface HandleDocumentCreatePayload extends DocumentInfo {
  pushToEditor: boolean
  uploadFromDemo?: boolean
}
export interface DocumentFile {
  name: string
}

export interface MergeChecksToStatePayload {
  comments: Array<CardContent | CardContentWithoutEK>
  editorState?: EditorState
}

export interface ExternalLinkPayload {
  source: string
}

type ExternalLinkAdditionalDataType = OwlLinkAdditionalData

export interface ExternalLinkMeta {
  source?: string
  additionalData?: ExternalLinkAdditionalDataType
}

export interface CreatePaperPayload {
  editorState: EditorState
}

export interface LoadModulesPayload {
  editorState: EditorState
}

export interface ClickNavLinkPayload {
  label?: string
}

export const GET_PAPER_SUBJECT = 'document/GET_PAPER_SUBJECT'
export const GET_PAPER_SUBJECT_SUCCESS = 'document/GET_PAPER_SUBJECT_SUCCESS'
export const GET_PAPER_SUBJECT_FAIL = 'document/GET_PAPER_SUBJECT_FAIL'

export const DOCUMENT_READY = 'document/DOCUMENT_READY'

export const HANDLE_DOCUMENT_CREATE = 'document/HANDLE_DOCUMENT_CREATE'
export const DOCUMENT_CREATE_COMPLETE = 'document/DOCUMENT_CREATE_COMPLETE'
export const SET_DOCUMENT = 'document/SET_DOCUMENT'
export const GET_DOCUMENT = 'document/GET_DOCUMENT'
export const GET_DOCUMENT_FAIL = 'document/GET_DOCUMENT_FAIL'
export const SET_EDITOR_STATE = 'document/SET_EDITOR_STATE'
export const SET_FILENAME = 'document/SET_FILENAME'

export const GET_PLAIN_TEXT = 'document/GET_PLAIN_TEXT'
export const SET_PLAIN_TEXT = 'document/SET_PLAIN_TEXT'
export const GET_PLAIN_TEXT_FAIL = 'document/GET_PLAIN_TEXT_FAIL'
export const GENERATE_EDITOR_STATE = 'document/GENERATE_EDITOR_STATE'

export const CREATE_PAPER = 'document/CREATE_PAPER'
export const CREATE_PAPER_SUCCESS = 'document/CREATE_PAPER_SUCCESS'
export const CREATE_PAPER_FAIL = 'document/CREATE_PAPER_FAIL'

export const COPY_TEXT = 'document/COPY_TEXT'
export const TEXT_COPIED = 'document/TEXT_COPIED'
export const COPY_RESET = 'document/COPY_RESET'

export const DOWNLOAD_PAPER = 'document/DOWNLOAD_PAPER'

export const GENERATE_EDITOR_STATE_FROM_PLAINTEXT =
  'document/GENERATE_EDITOR_STATE_FROM_PLAINTEXT'
export const LOAD_MODULES = 'document/LOAD_MODULES'

export const SEE_SOURCES = 'document/SEE_SOURCES'

export const generateEditorState = createStandardAction(GENERATE_EDITOR_STATE)<
  RawDraftContentState
>()

export const loadModules = createStandardAction(LOAD_MODULES)<LoadModulesPayload>()

export const generateEditorStateFromPlainText = createStandardAction(
  GENERATE_EDITOR_STATE_FROM_PLAINTEXT
)<PlainTextPayload>()

export const getPlainTextDocument = createStandardAction(GET_PLAIN_TEXT)<DocumentInfo>()

export const GET_DOCUMENT_SUCCESS = 'document/GET_DOCUMENT_SUCCESS'

export const SAVE_DOCUMENT = 'document/SAVE_DOCUMENT'
export const SAVE_DOCUMENT_SUCCESS = 'document/SAVE_DOCUMENT_SUCCESS'
export const SAVE_DOCUMENT_FAIL = 'document/SAVE_DOCUMENT_FAIL'
export const SET_ACTIVE_COMMENT_ID = 'document/SET_ACTIVE_COMMENT_ID'
export const SET_ACTIVE_ENTITY_KEY = 'document/SET_ACTIVE_ENTITY_KEY'
export const CLEAR_ACTIVE_COMMENT_ID = 'document/CLEAR_ACTIVE_COMMENT_ID'

export const MERGE_CHECKS_TO_STATE = 'document/MERGE_CHECKS_TO_STATE'

export const OPEN_EXTERNAL_LINK = 'document/OPEN_EXTERNAL_LINK'

export const FILTER_OVERLAPS = 'document/FILTER_OVERLAPS'
export const GATE_COMMENTS = 'document/GATE_COMMENTS'
export const SHOW_GATED_COMMENTS = 'document/SHOW_GATED_COMMENTS'

export const RECHECK = 'document/RECHECK'
export const UNDO = 'document/UNDO'

export const CLICK_NAV_LINK = 'document/CLICK_NAV_LINK'

export const documentReady = createStandardAction(DOCUMENT_READY)()

export const handleDocumentCreate = createStandardAction(HANDLE_DOCUMENT_CREATE)<
  HandleDocumentCreatePayload
>()

export const setDocument = createStandardAction(SET_DOCUMENT)<DocumentInfo>()
export const setFilename = createStandardAction(SET_FILENAME)<DocumentFile>()

export const copyText = createStandardAction(COPY_TEXT)()
export const copyReset = createStandardAction(COPY_RESET)()
export const textCopied = createStandardAction(TEXT_COPIED)()

export const downloadPaper = createStandardAction(DOWNLOAD_PAPER)()

export const getDocument = createAsyncAction(
  GET_DOCUMENT,
  GET_DOCUMENT_SUCCESS,
  GET_DOCUMENT_FAIL
)<DocumentInfo, DocumentInfo, Error>()

export interface SaveDocumentPayload {
  id?: string
  editorState?: EditorState
  manual?: boolean
  document?: DocumentState
}

export interface EditorStatePayload {
  editorState: EditorState
}
export const setEditorState = createStandardAction(SET_EDITOR_STATE)<EditorStatePayload>()

export interface PlainTextPayload {
  text: string
  id: string
}

export interface ActiveEntityKeyPayload {
  key: string
  component: string
}

export interface ActiveEntityKeyMeta {
  isClick?: boolean
  type?: string
}

export interface ActiveCommentIdPayload {
  id: string
  sharedId?: string
  component: string
}

export interface SeeSourcesPayload {
  metadataId: string
}

export interface ShowGatedCommentsPayload {
  editorState?: EditorState
}

export const getPlainTextFail = createStandardAction(GET_PLAIN_TEXT_FAIL)<Error>()
export const setActiveEntityKey = createStandardAction(SET_ACTIVE_ENTITY_KEY)<
  ActiveEntityKeyPayload,
  ActiveEntityKeyMeta
>()

export const setActiveCommentId = createStandardAction(SET_ACTIVE_COMMENT_ID)<
  ActiveCommentIdPayload
>()

export const clearActiveCommentId = createStandardAction(CLEAR_ACTIVE_COMMENT_ID)()

export const saveDocument = createAsyncAction(
  SAVE_DOCUMENT,
  SAVE_DOCUMENT_SUCCESS,
  SAVE_DOCUMENT_FAIL
)<SaveDocumentPayload, {}, Error>()

export const mergeChecksToState = createStandardAction(MERGE_CHECKS_TO_STATE)<
  MergeChecksToStatePayload
>()

export const openExternalLink = createStandardAction(OPEN_EXTERNAL_LINK)<
  ExternalLinkPayload,
  ExternalLinkMeta
>()

export const seeSources = createStandardAction(SEE_SOURCES)<SeeSourcesPayload>()
export const filterOverlaps = createStandardAction(FILTER_OVERLAPS)()
export const gateComments = createStandardAction(GATE_COMMENTS)()
export const showGatedComments = createStandardAction(SHOW_GATED_COMMENTS)<
  ShowGatedCommentsPayload
>()
export const createPaper = createStandardAction(CREATE_PAPER)<CreatePaperPayload>()
export const createPaperSuccess = createStandardAction(CREATE_PAPER_SUCCESS)()
export const createPaperFailure = createStandardAction(CREATE_PAPER_FAIL)<Error>()

export interface RecheckPayload {
  category: CheckCategory
}
export const recheck = createStandardAction(RECHECK)<RecheckPayload>()
export const undo = createStandardAction(UNDO)()

export const onClickNavLink = createStandardAction(CLICK_NAV_LINK)<ClickNavLinkPayload>()

export interface GetPaperSubjectRequest {
  text: string
}

export interface GetPaperSubjectResponse {
  subject: string
  score: number
  kgName: string
  kgUuid: string | null
  metadata: Partial<
    Omit<GetPaperSubject_getPaperSubjectPrediction_metadata, '__typename'>
  > | null
}

export const getPaperSubject = createAsyncAction(
  GET_PAPER_SUBJECT,
  GET_PAPER_SUBJECT_SUCCESS,
  GET_PAPER_SUBJECT_FAIL
)<GetPaperSubjectRequest, GetPaperSubjectResponse, undefined>()

export const ADD_COMMENTS = 'document/ADD_COMMENTS'
export const SET_COMMENTS = 'document/SET_COMMENTS'

export interface CommentsMap {
  [id: string]: CardContent
}

export const addComments = createStandardAction(ADD_COMMENTS)<CommentsMap>()
export const setComments = createStandardAction(SET_COMMENTS)<CommentsMap>()

export const UPDATE_COMMENT = 'document/UPDATE_COMMENT'
export const UPDATE_COMMENTS = 'document/UPDATE_COMMENTS'
export const updateComment = createStandardAction(UPDATE_COMMENT)<CardContent>()
export const updateComments = createStandardAction(UPDATE_COMMENTS)<CardContent[]>()

export const DELETE_COMMENT = 'document/DELETE_COMMENT'
export const DELETE_COMMENTS = 'document/DELETE_COMMENTS'

export const deleteComment = createStandardAction(DELETE_COMMENT)<{ id: string }>()
export const deleteComments = createStandardAction(DELETE_COMMENTS)<{ ids: string[] }>()

export const DELETE_ALL_GRAMMAR_COMMENT = 'document/DELETE_ALL_GRAMMAR_COMMENT'
export const DELETE_ALL_PLAGIARISM_COMMENT = 'document/DELETE_ALL_PLAGIARISM_COMMENT'

export const deleteAllGrammarComments = createStandardAction(DELETE_ALL_GRAMMAR_COMMENT)()
export const deleteAllPlagiarismComments = createStandardAction(
  DELETE_ALL_PLAGIARISM_COMMENT
)()

export const SET_PLAGIARISM_SCORE = 'document/SET_PLAGIARISM_SCORE'
export const DELETE_PLAGIARISM_SCORE = 'document/DELETE_PLAGIARISM_SCORE'
export const setPlagiarismScore = createStandardAction(SET_PLAGIARISM_SCORE)<{
  score: number
}>()
export const deletePlagiarismScore = createStandardAction(DELETE_PLAGIARISM_SCORE)()

const CLEAR_DOCUMENT = 'document/CLEAR_DOCUMENT'
export const clearDocument = createStandardAction(CLEAR_DOCUMENT)()



// WEBPACK FOOTER //
// ./src/state/document/actions.ts